import * as THREE from 'three';
import { ClipTask } from '../defines';
import { BoxVolume } from '../utils/Volume';
import type { Viewer } from './viewer';
import type { PointCloudOctree } from '../PointCloudOctree';
import { ClusterPersonaTool } from '../personas/tools/implementations/ClusterPersonaTool';

// Structurally compatible with PointCloudMaterial's file-local interfaces.
// We mirror them here because PointCloudMaterial.ts doesn't export them.
export interface ClipBoxData {
  box: THREE.Object3D & { getActualClipTask(): number };
  inverse: THREE.Matrix4;
  position: THREE.Vector3;
}

export interface ClipPlaneData {
  plane: THREE.Object3D & { actualClipTask: number; heatmapIsolate?: boolean };
  inverse: THREE.Matrix4;
  position: THREE.Vector3;
}

export interface ClipState {
  clipBoxes: ClipBoxData[];
  clipPolygons: unknown[];
  clipPlanes: ClipPlaneData[];
  /** Per-pointcloud clustered-segment list, keyed by pointcloud identifier. */
  clusteredSegmentsByPointCloud: Map<string, unknown[]>;
}

/**
 * A view's resolved floor-isolation clip — a plane OR a box, never both.
 * Held per-RenderView and merged into that view's clip state at render time
 * (`mergeClipFragment`). Built by `resolveStrategyToClipFragment` without
 * creating scene objects (no reactive props, no geometry).
 */
export interface IsolationClipFragment {
  clipBoxes: ClipBoxData[];
  clipPlanes: ClipPlaneData[];
}

interface ClippablePlane extends THREE.Object3D {
  actualClipTask: number;
  matrixWorld: THREE.Matrix4;
}

/**
 * Compute the per-frame clipping state from the viewer's scene + active personas.
 *
 * This used to live inline in viewer.update() and mutate every pointcloud's
 * material directly. Splitting compute from apply is what makes per-RenderView
 * clip overrides possible later: alternate clip state can be built without
 * scanning the whole scene graph again.
 */
export function buildClipState(viewer: Viewer): ClipState {
  const scene = viewer.scene;

  // Box volumes with .clip set
  const boxes: (THREE.Object3D & { matrixWorld: THREE.Matrix4; getActualClipTask(): number })[] = [];
  boxes.push(...scene.volumes.filter((v) => v.clip && v instanceof BoxVolume));

  // determinant === 0 ⇒ matrixWorld can't be inverted; skip those
  const degenerate = (box: THREE.Object3D) => box.matrixWorld.determinant() !== 0;

  const clipBoxes: ClipBoxData[] = boxes.filter(degenerate).map((box) => {
    box.updateMatrixWorld();
    return {
      box,
      inverse: box.matrixWorld.clone().invert(),
      position: box.getWorldPosition(new THREE.Vector3()),
    };
  });

  const clipPolygons = scene.polygonClipVolumes.filter((vol) => (vol as THREE.Object3D & { initialized?: boolean }).initialized);

  // Planes that act as clip planes — pulled from many sources
  const planes: (ClippablePlane | null | undefined)[] = [];
  planes.push(...scene.planes.filter((p) => p.actualClipTask !== ClipTask.NONE));
  planes.push(...scene.surfaces.filter((s) => s.actualClipTask !== ClipTask.NONE));
  if (viewer.viewerMode.hasVirtualPlane) planes.push(viewer.viewerMode.associatedPlane as ClippablePlane);
  if (viewer.floorplanTool.active && viewer.floorplanTool.activeState) {
    planes.push(viewer.floorplanTool.activeState.plane as ClippablePlane);
  }

  const activePersona = viewer.personaManager?.getActivePersona();
  if (activePersona) {
    const planeTool = activePersona.tools.get('plane') as { enabled?: boolean; activePlane?: ClippablePlane & { visible: boolean } } | undefined;
    if (planeTool?.enabled && planeTool.activePlane?.visible) {
      planes.push(planeTool.activePlane);
    }
    const lineTool = activePersona.tools.get('line_detection') as typeof planeTool;
    if (lineTool?.enabled && lineTool.activePlane?.visible && lineTool.activePlane?.actualClipTask !== ClipTask.NONE) {
      planes.push(lineTool.activePlane);
    }
  }

  const clipPlanes: ClipPlaneData[] = planes
    .filter((p): p is ClippablePlane => !!p && p.matrixWorld.determinant() !== 0)
    .map((plane) => {
      plane.updateMatrixWorld();
      return {
        plane: plane as ClipPlaneData['plane'],
        inverse: plane.matrixWorld.clone().invert(),
        position: plane.getWorldPosition(new THREE.Vector3()),
      };
    });

  // Per-pointcloud clustered segments. Computed here so the apply step is
  // a pure write — no scene queries during render.
  const clusteredSegmentsByPointCloud = new Map<string, unknown[]>();
  const clusterTool = activePersona?.tools.get('cluster') as ClusterPersonaTool | undefined;
  for (const pointcloud of scene.pointclouds) {
    const id = pointcloud.identifier ?? '';
    const segments = scene.pointClusters.flatMap((cluster) => cluster.filterSegmentsByPointCloud(id));
    if (clusterTool?.enabled) {
      segments.push(...(clusterTool.getClustersForPointCloud(id) as typeof segments));
    }
    clusteredSegmentsByPointCloud.set(id, segments);
  }

  return { clipBoxes, clipPolygons, clipPlanes, clusteredSegmentsByPointCloud };
}

/**
 * Push a ClipState onto every pointcloud's material. Mirrors the original
 * inline mutation in viewer.update().
 *
 * The clip setters re-run shader source generation when the count changes,
 * so calling this every frame with the same data is cheap (no recompile),
 * but switching between two views with different counts will recompile.
 * Acceptable for v1; revisit if the widget shipped clip-override starts to
 * pin the shader compiler.
 */
export function applyClipState(pointclouds: PointCloudOctree[], state: ClipState, maxPolygonVertices: number): void {
  for (const pointcloud of pointclouds) {
    pointcloud.material.setClipBoxes(state.clipBoxes);
    pointcloud.material.setClipPolygons(state.clipPolygons, maxPolygonVertices);
    pointcloud.material.setClipPlanes(state.clipPlanes);

    const segments = state.clusteredSegmentsByPointCloud.get(pointcloud.identifier ?? '') ?? [];
    pointcloud.material.setClusteredSegments(segments as Parameters<typeof pointcloud.material.setClusteredSegments>[0]);
  }
}

/**
 * Identifier resolver for clip volumes.
 *
 * BoxVolume / PointoramaPlane / PointoramaSurface have a stable `.identifier`
 * field (uuid by default). PolygonClipVolume inherits Object3D's `.uuid` and
 * we fall back to that. Anything else returns `undefined` — those entries
 * always pass the mask filter (effectively "always enabled").
 */
function clipObjectId(obj: unknown): string | undefined {
  if (!obj || typeof obj !== 'object') return undefined;
  const o = obj as { identifier?: unknown; uuid?: unknown };
  if (typeof o.identifier === 'string') return o.identifier;
  if (typeof o.uuid === 'string') return o.uuid;
  return undefined;
}

/**
 * Apply a per-view scene-clip mask to an existing ClipState.
 *
 * Items with no resolvable identifier pass through unchanged. Items whose
 * identifier maps to `false` in the mask are dropped. Missing keys default
 * to enabled — adding a new clip volume to the scene is visible everywhere
 * by default.
 *
 * Returns a new ClipState; the input is not mutated. Cluster segments are
 * not affected (they aren't user-toggleable clip volumes).
 */
export function filterClipStateByMask(state: ClipState, mask: { readonly [id: string]: boolean }): ClipState {
  const passes = (obj: unknown): boolean => {
    const id = clipObjectId(obj);
    if (id === undefined) return true;
    return mask[id] !== false;
  };

  return {
    clipBoxes: state.clipBoxes.filter((b) => passes(b.box)),
    clipPolygons: state.clipPolygons.filter((p) => passes(p)),
    clipPlanes: state.clipPlanes.filter((p) => passes(p.plane)),
    clusteredSegmentsByPointCloud: state.clusteredSegmentsByPointCloud,
  };
}

/**
 * Append a per-view floor-isolation fragment onto a clip state. Returns a new
 * ClipState; `clipPolygons` and the cluster map are carried by reference.
 * `null` fragment is a no-op (returns the base unchanged).
 */
export function mergeClipFragment(base: ClipState, fragment: IsolationClipFragment | null): ClipState {
  if (!fragment) return base;
  return {
    clipBoxes: fragment.clipBoxes.length ? [...base.clipBoxes, ...fragment.clipBoxes] : base.clipBoxes,
    clipPolygons: base.clipPolygons,
    clipPlanes: fragment.clipPlanes.length ? [...base.clipPlanes, ...fragment.clipPlanes] : base.clipPlanes,
    clusteredSegmentsByPointCloud: base.clusteredSegmentsByPointCloud,
  };
}
